import { memo, useState } from 'react'
import { sanitize } from '@/lib/html'
import { renderChatMarkdown } from '../markdown'
import type { ChatMessagePart } from '../types'
import { TableBlock } from './TableBlock'
import { ChartBlock } from './ChartBlock'
import { CustomChartBlock } from './CustomChartBlock'
import { ArtifactCard } from './ArtifactCard'
import { FinancialReportBlock } from './FinancialReportBlock'
import { SharePartDialog } from '../SharePartDialog'

interface Props {
  parts: ChatMessagePart[] | null | undefined
  /**
   * Identifier for the persisted chat_messages row. When set, each
   * structured part gets a "Share" icon that creates a scoped public
   * link via SharePartDialog. Missing on live-streaming assistant
   * turns - those parts go shareable after the message is persisted.
   */
  messageId?: string
  /** Thread the message belongs to - required for scoped shares. */
  threadId?: string | null
}

function PartsRendererInner({ parts, messageId, threadId }: Props) {
  const [sharing, setSharing] = useState<{ index: number; kind: string } | null>(null)
  if (!parts || parts.length === 0) return null

  const canShare = !!messageId && !!threadId
  // Shaped into a stable callback factory so each block can receive a
  // concrete `onShare` prop (rather than an absolute-positioned overlay
  // that fought with the block's own action rail).
  const shareFor = (index: number, kind: string) =>
    canShare ? () => setSharing({ index, kind }) : undefined

  return (
    <>
      {parts.map((p, i) => {
        if (p.kind === 'text') {
          if (!p.text) return null
          return (
            <div
              key={`seg-${i}`}
              className="prose-sm max-w-none [&_a]:text-primary [&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-xs [&_li]:my-1 [&_p]:my-1 [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-muted [&_pre]:p-3 [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:pl-5"
              // eslint-disable-next-line react/no-danger -- sanitized via DOMPurify in lib/html.ts
              dangerouslySetInnerHTML={{ __html: sanitize(renderChatMarkdown(p.text)) }}
            />
          )
        }

        if (p.kind === 'table')
          return <TableBlock key={`seg-${i}`} part={p} onShare={shareFor(i, p.kind)} />
        if (p.kind === 'chart')
          return <ChartBlock key={`seg-${i}`} part={p} onShare={shareFor(i, p.kind)} />
        if (p.kind === 'custom_chart')
          return <CustomChartBlock key={`seg-${i}`} part={p} onShare={shareFor(i, p.kind)} />
        if (p.kind === 'artifact')
          return <ArtifactCard key={`seg-${i}`} part={p} onShare={shareFor(i, p.kind)} />
        if (p.kind === 'financial_report')
          return (
            <FinancialReportBlock key={`seg-${i}`} part={p} onShare={shareFor(i, p.kind)} />
          )
        return null
      })}
      {sharing && canShare ? (
        <SharePartDialog
          open
          threadId={threadId!}
          messageId={messageId!}
          partIndex={sharing.index}
          partKind={sharing.kind}
          onClose={() => setSharing(null)}
        />
      ) : null}
    </>
  )
}

// Charts inside parts use Chart.js on a <canvas>. Without memo, the parent
// ChatbotPage re-renders on every keystroke in the input bar and forces every
// historical chart to re-paint, which the user perceives as flicker. Identity
// of `parts` is stable for persisted messages, so a shallow prop check is the
// right gate here.
export const PartsRenderer = memo(PartsRendererInner)
